<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { rulersVisible } from './rulerState';
import { computeRulerMetrics, niceTickStep, type RulerMetrics } from './rulerMath';

const props = defineProps<{
  context: {
    viewName: string;
    config: Record<string, unknown>;
  };
}>();

const root = ref<HTMLDivElement | null>(null);
const canvas = ref<HTMLCanvasElement | null>(null);
let observer: ResizeObserver | null = null;
let frame = 0;
let lastVisualSize = '';

const itemsConfig = computed(() => (props.context.config.items ?? {}) as Record<string, unknown>);
const metrics = computed(() => computeRulerMetrics({
  viewName: props.context.viewName,
  areas: itemsConfig.value.area as Record<string, unknown> | undefined,
  params: itemsConfig.value.params as Record<string, unknown> | undefined,
  stageWidth: Number(itemsConfig.value.boxWidth ?? 500),
  stageHeight: Number(itemsConfig.value.boxHeight ?? 500),
}));

let warnedUnconfiguredView = '';

const TARGET_MAJOR_PX = 36; // desired on-screen spacing between labeled ticks
const MIN_MINOR_PX = 5; // below this, skip minor ticks entirely

interface TickPlan {
  majorStep: number;
  minorStep: number;
  labelDecimals: number;
}

function tickPlan(pxPerCm: number): TickPlan {
  const majorStep = niceTickStep(TARGET_MAJOR_PX / Math.max(0.0001, pxPerCm));
  const fraction = majorStep / 10 ** Math.floor(Math.log10(majorStep));
  const minorDivisions = Math.abs(fraction - 2) < 0.001 ? 2 : 5; // 1,5→5 minors; 2→2 minors
  const minorStep = majorStep / minorDivisions;
  const showMinor = minorStep * pxPerCm >= MIN_MINOR_PX;
  const labelDecimals = majorStep < 1 ? Math.max(0, -Math.floor(Math.log10(majorStep))) : 0;
  return { majorStep, minorStep: showMinor ? minorStep : majorStep, labelDecimals };
}

function drawTickAxis(
  lengthCm: number,
  pxPerCm: number,
  drawTick: (x: number, major: boolean, label: string) => void,
) {
  const plan = tickPlan(pxPerCm);
  for (let cm = 0; cm <= lengthCm + 0.001; cm += plan.minorStep) {
    const major = Math.abs(cm / plan.majorStep - Math.round(cm / plan.majorStep)) < 0.001;
    drawTick(cm * pxPerCm, major, major ? cm.toFixed(plan.labelDecimals) : '');
  }
}

function drawCmTicks(ctx: CanvasRenderingContext2D, metric: RulerMetrics) {
  const area = metric.printArea;
  const topY = Math.max(0, area.top - 24);
  const leftX = Math.max(0, area.left - 24);
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.strokeStyle = 'rgba(15, 23, 42, 0.18)';
  ctx.fillRect(area.left, topY, area.width, 24);
  ctx.fillRect(leftX, area.top, 24, area.height);
  ctx.strokeRect(area.left, topY, area.width, 24);
  ctx.strokeRect(leftX, area.top, 24, area.height);

  ctx.strokeStyle = 'rgba(2, 132, 199, 0.9)';
  ctx.fillStyle = 'rgba(2, 132, 199, 0.95)';
  ctx.font = '600 10px system-ui, sans-serif';
  ctx.fillText('cm', Math.max(area.left + 3, area.left + area.width - 18), topY + 20);
  ctx.font = '10px system-ui, sans-serif';
  drawTickAxis(metric.physicalCm.width, metric.pxPerCmX, (dx, major, label) => {
    const x = area.left + dx;
    ctx.beginPath();
    ctx.moveTo(x, topY + 24);
    ctx.lineTo(x, topY + (major ? 6 : 14));
    ctx.stroke();
    if (major) ctx.fillText(label, x + 2, topY + 11);
  });
  drawTickAxis(metric.physicalCm.height, metric.pxPerCmY, (dy, major, label) => {
    const y = area.top + dy;
    ctx.beginPath();
    ctx.moveTo(leftX + 24, y);
    ctx.lineTo(leftX + (major ? 6 : 14), y);
    ctx.stroke();
    if (major) ctx.fillText(label, leftX + 3, y - 2);
  });
}

function draw() {
  const host = root.value;
  const node = canvas.value;
  if (!host || !node || !rulersVisible.value) return;
  const width = Math.max(1, Math.round(host.offsetWidth || Number(itemsConfig.value.boxWidth) || 500));
  const height = Math.max(1, Math.round(host.offsetHeight || Number(itemsConfig.value.boxHeight) || 500));
  const rect = host.getBoundingClientRect();
  const visualScale = Math.max(1, rect.width / width || 1, rect.height / height || 1);
  const dpr = Math.max(1, window.devicePixelRatio || 1);
  const backingScale = dpr * visualScale;
  node.style.width = `${width}px`;
  node.style.height = `${height}px`;
  node.width = Math.round(width * backingScale);
  node.height = Math.round(height * backingScale);
  const ctx = node.getContext('2d');
  if (!ctx) return;
  ctx.setTransform(backingScale, 0, 0, backingScale, 0, 0);
  ctx.clearRect(0, 0, width, height);
  if (!metrics.value.configured) {
    if (warnedUnconfiguredView !== props.context.viewName) {
      warnedUnconfiguredView = props.context.viewName;
      console.warn(`[veyo-product-personalizer] Rulers hidden for view "${props.context.viewName}": no "Printable size (cm)" set for this product view in the admin editor.`);
    }
    return;
  }
  drawCmTicks(ctx, metrics.value);
}

function watchVisualSize() {
  const host = root.value;
  if (host && rulersVisible.value) {
    const rect = host.getBoundingClientRect();
    const next = `${rect.width.toFixed(2)}x${rect.height.toFixed(2)}`;
    if (next !== lastVisualSize) {
      lastVisualSize = next;
      draw();
    }
  }
  frame = requestAnimationFrame(watchVisualSize);
}

watch([rulersVisible, metrics], () => {
  void nextTick(draw);
}, { deep: true });

onMounted(() => {
  observer = new ResizeObserver(() => draw());
  if (root.value) observer.observe(root.value);
  window.addEventListener('resize', draw);
  frame = requestAnimationFrame(watchVisualSize);
  void nextTick(draw);
});

onBeforeUnmount(() => {
  observer?.disconnect();
  cancelAnimationFrame(frame);
  window.removeEventListener('resize', draw);
});
</script>

<template>
  <div v-show="rulersVisible" ref="root" class="vptw:absolute vptw:inset-0 vptw:pointer-events-none" aria-hidden="true">
    <canvas ref="canvas" class="vptw:absolute vptw:inset-0 vptw:h-full vptw:w-full" />
  </div>
</template>
